/**
 * Образующие фуксовой группы и преобразования склейки фундаментального
 * многоугольника (модель Пуанкаре).
 *
 * Для каждой пары отождествляемых сторон вычисляется единственная сохраняющая
 * ориентацию изометрия диска, переводящая одну сторону в парную с заданной
 * ориентацией. Изометрия диска однозначно определяется образом упорядоченной
 * пары точек (3 вещественных параметра SU(1,1)); поскольку гиперболические
 * длины парных сторон равны, отображение концов стороны корректно и однозначно.
 *
 * Для стороны `s` с концами `(p0, p1)` и парной `s'` с концами `(q0, q1)`
 * строится `g`, такая что `g(p0) = q1` и `g(p1) = q0` (обращение концов
 * из-за противоположной ориентации при склейке). Результат — образующие
 * (по одной на пару сторон) и их обратные; группа
 * `Γ = ⟨a, b, c, d | abABcdCD = 1⟩` для рода 2.
 *
 * Все функции чистые и детерминированные, без DOM.
 *
 * Требования: 2.5, 2.7
 */

import type { Complex, FundamentalPolygon, Isometry, Mobius, SidePairingSpec } from './map-types.js';
import type { Geometry } from './geometry.js';
import { hyperbolicGeometry, isometryIdentity } from './geometry.js';
import { argument, complexEquals, conjugate, modulus, negate } from './complex.js';
import {
  apply,
  chain,
  compose,
  determinant,
  identity,
  invert,
  mobiusEquals,
  normalize,
  rotation,
  translateToOrigin,
} from './mobius.js';
import { GENUS2_PAIRING_WORD } from './fundamental-polygon.js';

/** Допуск проверки того, что образующая переводит сторону в парную. */
export const PAIRING_TOLERANCE = 1e-7;

/**
 * Устаревшее (некорректное) слово склейки восьмиугольника рода 2.
 *
 * Ранее для рода 2 использовалось слово `abcdABCD` (склейка по противоположным
 * сторонам). Оно заменено на коммутаторное {@link GENUS2_PAIRING_WORD}
 * (`abABcdCD`, Требование 7.4). Значение хранится для защитной нормализации
 * ранее сохранённых конфигураций.
 */
export const LEGACY_GENUS2_PAIRING_WORD = 'abcdABCD';

/**
 * Допуск проверки группового соотношения рода 2 (Требования 7.2, 7.3):
 * норма отклонения произведения образующих от тождества.
 */
export const GROUP_RELATION_TOLERANCE = 1e-6;

/**
 * Нормализовать слово склейки при чтении конфигурации (Требование 7.4).
 *
 * Устаревшее слово рода 2 `abcdABCD` заменяется на корректное коммутаторное
 * слово `abABcdCD` ({@link GENUS2_PAIRING_WORD}). Все прочие слова возвращаются
 * без изменений. Нормализация защитная: применяется в {@link computeSidePairing},
 * поэтому ранее сохранённые карты со старым словом читаются как карты с новым.
 *
 * @param word слово склейки из конфигурации карты
 * @returns нормализованное слово склейки
 */
export function normalizePairingWord(word: string): string {
  return word === LEGACY_GENUS2_PAIRING_WORD ? GENUS2_PAIRING_WORD : word;
}

/**
 * Описание одной образующей фуксовой группы — изометрии, отождествляющей
 * пару сторон фундаментального многоугольника.
 */
export interface Generator {
  /** Метка образующей (строчная буква, например `'a'`). */
  label: string;
  /** Метка обратной образующей (заглавная буква, например `'A'`). */
  inverseLabel: string;
  /** Изометрия диска, переводящая исходную сторону в парную. */
  transform: Mobius;
  /** Индекс исходной стороны (с меткой `label`). */
  sourceSide: number;
  /** Индекс парной стороны (с меткой `inverseLabel`). */
  targetSide: number;
  /**
   * `true`, если образующая отождествляет пару сторон с обращением ориентации
   * (задана парой одинаковых строчных букв, например `aa`) — образующая
   * антиголоморфна (Требование 8.1). По умолчанию `false` (пара `x…X`,
   * сохраняющая ориентацию).
   */
  orientationReversing: boolean;
}

/**
 * Результат вычисления склейки сторон.
 */
export interface SidePairingResult {
  /** Образующие по одной на каждую пару сторон (в порядке появления букв в слове). */
  generators: Generator[];
  /**
   * Преобразование по метке: строчные буквы → образующие,
   * заглавные буквы → обратные образующие.
   */
  byLabel: Map<string, Mobius>;
  /**
   * Все преобразования (образующие и их обратные) единым массивом —
   * удобно для перечисления соседних плиток при замощении (задача 5.2).
   */
  all: Mobius[];
  /**
   * Ориентируемость поверхности (Требование 8.1). `true`, если все пары сторон
   * отождествляются с сохранением ориентации (`x…X`); `false`, если слово
   * склейки содержит хотя бы одну пару одинаковых строчных букв (`x…x`),
   * задающую отождествление с обращением ориентации.
   */
  orientable: boolean;
}

/**
 * Построить сохраняющую ориентацию изометрию диска, переводящую упорядоченную
 * пару точек `(p0, p1)` в `(q0, q1)`.
 *
 * Изометрия строится композицией стандартных блоков:
 * `g = h⁻¹ ∘ rotation(φ_q − φ_p) ∘ g0`, где
 * - `g0 = translateToOrigin(p0)` переносит `p0` в центр, образ `p1` лежит на
 *   радиусе под углом `φ_p`;
 * - `h  = translateToOrigin(q0)` переносит `q0` в центр, образ `q1` — под углом `φ_q`;
 * - поворот выравнивает направления, после чего `h⁻¹` возвращает пару к `(q0, q1)`.
 *
 * Отображение корректно (`g(p0) = q0`, `g(p1) = q1`) только если гиперболические
 * расстояния `d(p0, p1)` и `d(q0, q1)` равны; для парных сторон правильного
 * многоугольника это выполняется по построению.
 *
 * @param p0 первая точка-прообраз (|p0| < 1)
 * @param p1 вторая точка-прообраз (|p1| < 1)
 * @param q0 образ точки `p0` (|q0| < 1)
 * @param q1 образ точки `p1` (|q1| < 1)
 */
export function mapOrderedPair(
  p0: Complex,
  p1: Complex,
  q0: Complex,
  q1: Complex,
): Mobius {
  const g0 = translateToOrigin(p0);
  const phiP = argument(apply(g0, p1));
  const h = translateToOrigin(q0);
  const phiQ = argument(apply(h, q1));
  // Применяется справа налево: сначала g0, затем поворот, затем h⁻¹.
  const g = chain(invert(h), rotation(phiQ - phiP), g0);
  return normalize(g);
}

/**
 * Концы стороны `i` многоугольника: `(vertices[i], vertices[(i+1) mod n])`.
 */
function sideEndpoints(polygon: FundamentalPolygon, i: number): [Complex, Complex] {
  const n = polygon.vertices.length;
  return [polygon.vertices[i], polygon.vertices[(i + 1) % n]];
}

/**
 * Построить изометрию склейки для одной пары сторон: сторона `sourceSide`
 * с концами `(p0, p1)` переводится в парную `targetSide` с концами `(q0, q1)`
 * так, что `g(p0) = q1` и `g(p1) = q0` (обращение из-за противоположной
 * ориентации при склейке).
 */
function buildSidePairingTransform(
  polygon: FundamentalPolygon,
  sourceSide: number,
  targetSide: number,
): Mobius {
  const [p0, p1] = sideEndpoints(polygon, sourceSide);
  const [q0, q1] = sideEndpoints(polygon, targetSide);
  // Ориентация склейки обращается: g(p0) = q1, g(p1) = q0.
  return mapOrderedPair(p0, p1, q1, q0);
}

/**
 * Построить линейную часть `M` обращающей ориентацию изометрии склейки для
 * одной пары одинаковых строчных сторон (`x…x`, Требование 8.2).
 *
 * Изометрия антиголоморфна и действует как `T(z) = M(z̄)` (см.
 * {@link applyIsometry}). Требуется, чтобы `T` переводила упорядоченную пару
 * концов исходной стороны `(p0, p1)` в концы парной стороны `(q0, q1)` с
 * сохранением направления обхода границы (`T(p0) = q0`, `T(p1) = q1`);
 * обращение ориентации обеспечивается самим сопряжением, поэтому концы не
 * меняются местами (в отличие от голоморфного случая).
 *
 * Из `T(p_k) = M(p̄_k)` следует, что `M` — сохраняющая ориентацию изометрия
 * диска (SU(1,1)), переводящая `(p̄0, p̄1)` в `(q0, q1)`. Комплексное
 * сопряжение — изометрия диска, поэтому гиперболические длины сохраняются и
 * отображение упорядоченной пары корректно и однозначно
 * ({@link mapOrderedPair}).
 */
function buildReversingSidePairingTransform(
  polygon: FundamentalPolygon,
  sourceSide: number,
  targetSide: number,
): Mobius {
  const [p0, p1] = sideEndpoints(polygon, sourceSide);
  const [q0, q1] = sideEndpoints(polygon, targetSide);
  // T(z) = M(z̄); T(p0) = q0, T(p1) = q1  ⇒  M: (p̄0, p̄1) ↦ (q0, q1).
  return mapOrderedPair(conjugate(p0), conjugate(p1), q0, q1);
}

/**
 * Проверить, что обращающая ориентацию образующая (`x…x`) переводит каждую
 * вершину исходной стороны в соответствующую вершину парной стороны в пределах
 * `tolerance` (Требование 8.4), а её линейная часть `M` — изометрия диска
 * (`det M ≈ 1`), что гарантирует отрицательный определитель эффективной
 * (антиголоморфной) линейной части `−det M < 0` (Требования 8.2, 8.5).
 *
 * Образ вычисляется как `T(z) = M(z̄)` (сопряжение аргумента перед применением
 * матрицы), так что `T(p0) = q0`, `T(p1) = q1`.
 *
 * @throws Error если `M` не изометрия диска или вершины не совмещаются с парными.
 */
function verifyReversingSidePairing(
  polygon: FundamentalPolygon,
  gen: Generator,
  tolerance: number,
): void {
  const det = determinant(gen.transform);
  if (!Number.isFinite(det) || Math.abs(det - 1) > tolerance) {
    throw new Error(
      `Линейная часть обращающей ориентацию образующей '${gen.label}' не является ` +
        `изометрией диска (det = ${det})`,
    );
  }
  const [p0, p1] = sideEndpoints(polygon, gen.sourceSide);
  const [q0, q1] = sideEndpoints(polygon, gen.targetSide);
  // Антиголоморфное действие: сначала сопряжение аргумента, затем матрица M.
  const image0 = apply(gen.transform, conjugate(p0));
  const image1 = apply(gen.transform, conjugate(p1));
  const mapsInsideDisk = modulus(image0) < 1 && modulus(image1) < 1;
  if (
    !mapsInsideDisk ||
    !complexEquals(image0, q0, tolerance) ||
    !complexEquals(image1, q1, tolerance)
  ) {
    throw new Error(
      `Обращающая ориентацию образующая '${gen.label}' не переводит вершины ` +
        `стороны ${gen.sourceSide} в вершины парной стороны ${gen.targetSide} ` +
        `в пределах допуска ${tolerance}`,
    );
  }
}

/**
 * Проверить, что преобразование `g` — изометрия диска (унитарность SU(1,1),
 * `det ≈ 1`) и что оно переводит исходную сторону в парную с обращением
 * ориентации в пределах {@link PAIRING_TOLERANCE}.
 *
 * @throws Error если `g` не изометрия диска или не отображает сторону в парную.
 */
function verifySidePairing(
  polygon: FundamentalPolygon,
  gen: Generator,
  tolerance: number,
): void {
  const det = determinant(gen.transform);
  if (!Number.isFinite(det) || Math.abs(det - 1) > tolerance) {
    throw new Error(
      `Образующая '${gen.label}' не является изометрией диска (det = ${det})`,
    );
  }
  const [p0, p1] = sideEndpoints(polygon, gen.sourceSide);
  const [q0, q1] = sideEndpoints(polygon, gen.targetSide);
  const image0 = apply(gen.transform, p0);
  const image1 = apply(gen.transform, p1);
  const mapsInsideDisk = modulus(image0) < 1 && modulus(image1) < 1;
  if (
    !mapsInsideDisk ||
    !complexEquals(image0, q1, tolerance) ||
    !complexEquals(image1, q0, tolerance)
  ) {
    throw new Error(
      `Образующая '${gen.label}' не переводит сторону ${gen.sourceSide} ` +
        `в парную сторону ${gen.targetSide} с корректной ориентацией`,
    );
  }
}

/**
 * Описание разобранной пары сторон слова склейки.
 */
interface ParsedPair {
  /** Метка образующей (строчная буква). */
  label: string;
  /** Метка обратной образующей (заглавная буква). */
  inverseLabel: string;
  /** Индекс исходной стороны. */
  sourceSide: number;
  /** Индекс парной стороны. */
  targetSide: number;
  /**
   * `true`, если пара задана двумя одинаковыми строчными буквами (`x…x`) —
   * отождествление с обращением ориентации (Требование 8.1).
   */
  orientationReversing: boolean;
}

/**
 * Результат разбора слова склейки: список пар сторон и признак ориентируемости.
 */
interface ParsedPairing {
  /** Пары сторон в порядке первого появления буквы. */
  pairs: ParsedPair[];
  /** `false`, если есть хотя бы одна пара `x…x` (обращение ориентации). */
  orientable: boolean;
}

/**
 * Разобрать слово склейки и определить пары индексов сторон с ориентацией.
 *
 * Каждая сторона `i` помечается символом `word[i]`. Поддерживаются два вида
 * отождествления пары сторон одной буквы:
 * - **сохраняющее ориентацию** (`x…X`): одна строчная и одна заглавная сторона;
 *   образующая переводит строчную сторону в заглавную (голоморфно);
 * - **обращающее ориентацию** (`x…x`): две одинаковые строчные буквы без
 *   заглавной; пара помечается `orientationReversing: true`, а вся конфигурация
 *   становится неориентируемой (Требование 8.1).
 *
 * Порядок образующих задаётся порядком первого появления буквы.
 *
 * @throws Error если длина слова не равна числу сторон, встречены недопустимые
 *   символы или буква не образует корректную пару (`x…X` либо `x…x`).
 */
function parsePairing(word: string, sides: number): ParsedPairing {
  if (word.length !== sides) {
    throw new Error(
      `Длина слова склейки (${word.length}) не совпадает с числом сторон (${sides})`,
    );
  }

  const lowerIndices = new Map<string, number[]>();
  const upperIndices = new Map<string, number[]>();
  const order: string[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < word.length; i++) {
    const ch = word[i];
    const lower = ch.toLowerCase();
    const upper = ch.toUpperCase();
    if (lower === upper || !/[a-z]/.test(lower)) {
      throw new Error(`Недопустимый символ '${ch}' в слове склейки`);
    }
    if (!seen.has(lower)) {
      seen.add(lower);
      order.push(lower);
    }
    if (ch === lower) {
      const arr = lowerIndices.get(lower) ?? [];
      arr.push(i);
      lowerIndices.set(lower, arr);
    } else {
      const arr = upperIndices.get(lower) ?? [];
      arr.push(i);
      upperIndices.set(lower, arr);
    }
  }

  const pairs = order.map((letter): ParsedPair => {
    const lowers = lowerIndices.get(letter) ?? [];
    const uppers = upperIndices.get(letter) ?? [];

    // Сохраняющая ориентацию пара: одна строчная сторона и одна заглавная (x…X).
    if (lowers.length === 1 && uppers.length === 1) {
      return {
        label: letter,
        inverseLabel: letter.toUpperCase(),
        sourceSide: lowers[0],
        targetSide: uppers[0],
        orientationReversing: false,
      };
    }

    // Обращающая ориентацию пара: две одинаковые строчные буквы (x…x).
    if (lowers.length === 2 && uppers.length === 0) {
      return {
        label: letter,
        inverseLabel: letter.toUpperCase(),
        sourceSide: lowers[0],
        targetSide: lowers[1],
        orientationReversing: true,
      };
    }

    throw new Error(
      `Буква '${letter}' должна образовывать пару сторон: либо строчную и ` +
        `заглавную ('${letter}${letter.toUpperCase()}', сохранение ориентации), ` +
        `либо две строчные ('${letter}${letter}', обращение ориентации). ` +
        `Встречено строчных: ${lowers.length}, заглавных: ${uppers.length}.`,
    );
  });

  const orientable = pairs.every((pair) => !pair.orientationReversing);
  return { pairs, orientable };
}

/**
 * Вычислить преобразования склейки (образующие фуксовой группы) для
 * фундаментального многоугольника по схеме склейки.
 *
 * Для каждой строчной буквы слова строится изометрия диска, переводящая
 * её сторону в парную (заглавную) с обращением ориентации. Каждая образующая
 * проверяется на унитарность SU(1,1) и корректность отображения стороны;
 * при нарушении бросается ошибка (Требование 2.6 — отклонение некорректной
 * конфигурации). Возвращаются образующие, их обратные и удобные для замощения
 * представления.
 *
 * @param polygon фундаментальный многоугольник (вершины в порядке обхода)
 * @param pairingSpec схема склейки (слово, например `'abcdABCD'`)
 * @param tolerance допуск проверки склейки (по умолчанию {@link PAIRING_TOLERANCE})
 */
export function computeSidePairing(
  polygon: FundamentalPolygon,
  pairingSpec: SidePairingSpec,
  tolerance = PAIRING_TOLERANCE,
): SidePairingResult {
  // Защитная нормализация устаревшего слова рода 2 при чтении конфигурации
  // (Требование 7.4): `abcdABCD` → `abABcdCD`.
  const word = normalizePairingWord(pairingSpec.word);
  const { pairs, orientable } = parsePairing(word, polygon.vertices.length);

  const generators: Generator[] = [];
  const byLabel = new Map<string, Mobius>();
  const all: Mobius[] = [];

  for (const pair of pairs) {
    // Линейная часть `M` изометрии склейки.
    // - Сохраняющая ориентацию пара (`x…X`): голоморфная изометрия SU(1,1),
    //   действующая напрямую `z ↦ M(z)` (обмен концов из-за противоположной
    //   ориентации границы).
    // - Обращающая ориентацию пара (`x…x`, Требование 8.2): антиголоморфная
    //   изометрия `z ↦ M(z̄)` — сопряжение аргумента перед применением матрицы
    //   (`orientationReversing = true`); определитель эффективной линейной
    //   части отрицателен (отражение). `M` строится так, что каждая вершина
    //   исходной стороны переходит в соответствующую вершину парной.
    const transform = pair.orientationReversing
      ? buildReversingSidePairingTransform(polygon, pair.sourceSide, pair.targetSide)
      : buildSidePairingTransform(polygon, pair.sourceSide, pair.targetSide);
    const generator: Generator = {
      label: pair.label,
      inverseLabel: pair.inverseLabel,
      transform,
      sourceSide: pair.sourceSide,
      targetSide: pair.targetSide,
      orientationReversing: pair.orientationReversing,
    };
    if (pair.orientationReversing) {
      verifyReversingSidePairing(polygon, generator, tolerance);
    } else {
      verifySidePairing(polygon, generator, tolerance);
    }

    const inverse = invert(transform);
    generators.push(generator);
    byLabel.set(pair.label, transform);
    byLabel.set(pair.inverseLabel, inverse);
    all.push(transform, inverse);
  }

  return { generators, byLabel, all, orientable };
}

/**
 * Норма отклонения преобразования `m` от `n` по элементам матрицы SU(1,1).
 *
 * Учитывается знаковая неоднозначность SU(1,1) (`M` и `−M` задают одно
 * преобразование): берётся минимум расстояний до `n` и `−n`. Норма —
 * максимум евклидовых расстояний по компонентам `(α, β)`.
 */
function mobiusDeviation(m: Mobius, n: Mobius): number {
  const d = (a: Complex, b: Complex): number => Math.hypot(a.re - b.re, a.im - b.im);
  const same = Math.max(d(m.alpha, n.alpha), d(m.beta, n.beta));
  const opp = Math.max(d(m.alpha, negate(n.alpha)), d(m.beta, negate(n.beta)));
  return Math.min(same, opp);
}

/**
 * Результат проверки группового соотношения (Требования 7.2, 7.3).
 *
 * Дискриминируется полем `satisfied`. Функция-проверка чистая и не мутирует
 * состояние: при нарушении соотношения возвращается `satisfied: false` с
 * сообщением `error` — «признак ошибки нарушения соотношения», позволяющий
 * вызывающему коду отклонить конфигурацию и сохранить предыдущее корректное
 * состояние без изменений.
 */
export interface GroupRelationResult {
  /** `true`, если произведение образующих ≈ тождество в пределах допуска. */
  satisfied: boolean;
  /** Слово, по которому вычислялось произведение образующих. */
  word: string;
  /** Норма отклонения произведения от тождества (с учётом знака SU(1,1)). */
  deviation: number;
  /** Применённый допуск сравнения. */
  tolerance: number;
  /** Сообщение об ошибке при нарушении соотношения (иначе `undefined`). */
  error?: string;
}

/**
 * Проверить групповое соотношение: произведение образующих по заданному слову
 * должно равняться тождественному преобразованию (Требования 7.2, 7.3).
 *
 * Произведение вычисляется композицией преобразований по меткам слова
 * (строчные — образующие, заглавные — обратные) слева направо
 * ({@link chain}), затем сравнивается с тождеством через {@link mobiusEquals}
 * с допуском `tolerance`. Функция чистая: она не бросает исключение для
 * нарушенного соотношения и ничего не мутирует — вместо этого возвращает
 * структурированный признак ошибки, чтобы вызывающий код мог отклонить
 * конфигурацию, сохранив предыдущее валидное состояние (Требование 7.3).
 *
 * @param pairing результат {@link computeSidePairing}
 * @param word слово группового соотношения (по умолчанию `abABcdCD` для рода 2)
 * @param tolerance допуск сравнения с тождеством ({@link GROUP_RELATION_TOLERANCE})
 * @throws Error если в слове встречается метка, отсутствующая среди образующих
 */
export function verifyGroupRelation(
  pairing: SidePairingResult,
  word: string = GENUS2_PAIRING_WORD,
  tolerance = GROUP_RELATION_TOLERANCE,
): GroupRelationResult {
  const factors = Array.from(word, (label) => {
    const m = pairing.byLabel.get(label);
    if (!m) {
      throw new Error(
        `Нет преобразования для метки '${label}' в слове соотношения '${word}'`,
      );
    }
    return m;
  });

  const product = chain(...factors);
  const id = identity();
  const satisfied = mobiusEquals(product, id, tolerance);
  const deviation = mobiusDeviation(product, id);

  return {
    satisfied,
    word,
    deviation,
    tolerance,
    error: satisfied
      ? undefined
      : `Нарушено групповое соотношение '${word}': норма отклонения произведения ` +
        `образующих от тождественного преобразования (${deviation.toExponential(3)}) ` +
        `превышает допуск ${tolerance}.`,
  };
}

/**
 * Результат построения образующих с проверкой группового соотношения.
 */
export interface CheckedSidePairing {
  /** Построенные образующие фуксовой группы. */
  pairing: SidePairingResult;
  /** Результат проверки группового соотношения. */
  relation: GroupRelationResult;
}

/**
 * Построить образующие фуксовой группы для восьмиугольника рода 2 и проверить
 * групповое соотношение `abABcdCD` (Требования 7.2, 7.3).
 *
 * После построения образующих вычисляется произведение `a·b·A·B·c·d·C·D` и
 * сравнивается с тождеством с допуском {@link GROUP_RELATION_TOLERANCE}. Функция
 * не бросает исключение и не мутирует состояние: при нарушении соотношения
 * возвращается `relation.satisfied === false` с сообщением об ошибке, чтобы
 * вызывающий код мог отклонить конфигурацию и сохранить предыдущее корректное
 * состояние без изменений (Требование 7.3).
 *
 * @param polygon фундаментальный восьмиугольник рода 2
 * @param pairingSpec схема склейки (слово нормализуется в {@link computeSidePairing})
 * @param tolerance допуск проверки соотношения ({@link GROUP_RELATION_TOLERANCE})
 */
export function computeGenus2SidePairing(
  polygon: FundamentalPolygon,
  pairingSpec: SidePairingSpec,
  tolerance = GROUP_RELATION_TOLERANCE,
): CheckedSidePairing {
  const pairing = computeSidePairing(polygon, pairingSpec);
  const relation = verifyGroupRelation(pairing, GENUS2_PAIRING_WORD, tolerance);
  return { pairing, relation };
}

/** Допуск дедупликации элементов группы при переборе соседей (по близости матриц). */
export const DEFAULT_DEDUPE_TOLERANCE = 1e-6;

/**
 * Допуск совпадения вершин при вершинном обходе {@link enumerateVertexNeighbors}
 * (евклидово расстояние в координатах диска). Достаточно мал, чтобы не склеивать
 * различные вершины замощения, и достаточно велик, чтобы поглотить численный
 * дрейф композиции изометрий вдоль цикла вокруг вершины.
 */
export const DEFAULT_VERTEX_TOLERANCE = 1e-6;

/**
 * Страховочный предел длины слова для вершинного обхода
 * {@link enumerateVertexNeighbors}. Обход естественно завершается, когда фронт
 * перестаёт давать новые копии с общей вершиной (вершинное кольцо конечно);
 * этот предел лишь исключает бесконечный цикл при численных погрешностях и
 * заведомо превышает длину цикла вокруг вершины для поддерживаемых `{p, q}`.
 */
export const VERTEX_TRAVERSAL_SAFETY_DEPTH = 64;

/**
 * Параметры перечисления соседних плиток (элементов фуксовой группы `Γ`).
 */
export interface NeighborEnumerationOptions {
  /** Максимальная длина слова (глубина BFS по образующим); `≥ 0`. */
  maxDepth: number;
  /** Необязательное ограничение на число возвращаемых элементов. */
  maxCount?: number;
  /** Допуск дедупликации по близости матриц (по умолчанию {@link DEFAULT_DEDUPE_TOLERANCE}). */
  dedupeTolerance?: number;
  /**
   * Допуск совпадения вершин для вершинного обхода {@link enumerateVertexNeighbors}
   * (по умолчанию {@link DEFAULT_VERTEX_TOLERANCE}). Игнорируется обычным
   * BFS-перечислением по длине слова.
   */
  vertexTolerance?: number;
}

/**
 * Построить строковый ключ матрицы SU(1,1) для дедупликации по близости.
 *
 * Компоненты `(α, β)` квантуются на сетку с шагом `tolerance`. Учитывается
 * знаковая неоднозначность SU(1,1) (`M` и `−M` задают одно преобразование):
 * знак выбирается так, чтобы первый значимый компонент был неотрицательным,
 * что делает ключ каноническим независимо от знака матрицы.
 */
function mobiusKey(m: Mobius, tolerance: number): string {
  const comps = [m.alpha.re, m.alpha.im, m.beta.re, m.beta.im];
  let sign = 1;
  for (const c of comps) {
    if (Math.abs(c) > tolerance) {
      sign = c < 0 ? -1 : 1;
      break;
    }
  }
  const q = (x: number): number => {
    const r = Math.round((x * sign) / tolerance) * tolerance;
    return Object.is(r, -0) ? 0 : r;
  };
  return `${q(m.alpha.re)},${q(m.alpha.im)},${q(m.beta.re)},${q(m.beta.im)}`;
}

/**
 * Перечислить элементы фуксовой группы `Γ` (соседние плитки замощения) поиском
 * в ширину (BFS) по образующим и их обратным.
 *
 * Начиная с тождества (глубина 0), на каждом шаге текущий элемент домножается
 * (композицией) на каждое преобразование из {@link SidePairingResult.all},
 * порождая слова возрастающей длины. Так как группа бесконечна, перебор
 * ограничивается длиной слова `maxDepth` и (опционально) числом элементов
 * `maxCount`. Дубликаты (уже встреченные элементы группы) отбрасываются по
 * близости матриц: каждый кандидат нормируется {@link normalize} и хэшируется
 * каноническим ключом {@link mobiusKey}; повторные ключи пропускаются.
 *
 * Образ фундаментального многоугольника под каждым возвращённым элементом `γ`
 * даёт соседнюю плитку; фильтрация по видимой области выполняется на клиенте
 * (Требования 3.6, 4.2). Тождество всегда включается первым элементом.
 *
 * @param pairing результат {@link computeSidePairing} (используется массив `all`)
 * @param options параметры перебора (глубина, ограничение числа, допуск дедупликации)
 * @returns список уникальных элементов группы (включая тождество)
 */
export function enumerateNeighbors(
  pairing: SidePairingResult,
  options: NeighborEnumerationOptions,
): Mobius[] {
  const { maxDepth } = options;
  if (!Number.isInteger(maxDepth) || maxDepth < 0) {
    throw new Error(`maxDepth должен быть целым числом ≥ 0, получено: ${maxDepth}`);
  }
  const maxCount = options.maxCount ?? Number.POSITIVE_INFINITY;
  const tolerance = options.dedupeTolerance ?? DEFAULT_DEDUPE_TOLERANCE;
  const moves = pairing.all;

  const found: Mobius[] = [];
  const seen = new Set<string>();

  const id = identity();
  found.push(id);
  seen.add(mobiusKey(id, tolerance));

  let frontier: Mobius[] = [id];
  for (let depth = 1; depth <= maxDepth && found.length < maxCount; depth++) {
    const next: Mobius[] = [];
    for (const current of frontier) {
      if (found.length >= maxCount) break;
      for (const move of moves) {
        if (found.length >= maxCount) break;
        const candidate = normalize(compose(current, move));
        const key = mobiusKey(candidate, tolerance);
        if (seen.has(key)) continue;
        seen.add(key);
        found.push(candidate);
        next.push(candidate);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }

  return Number.isFinite(maxCount) ? found.slice(0, maxCount) : found;
}

/**
 * Применить преобразование `m` к каждой вершине фундаментального многоугольника,
 * получив образ-плитку (соседнюю копию области). Остальные поля (`sides`,
 * `preset`, `angleSum`) сохраняются: изометрия диска не меняет число сторон и
 * сумму углов. Удобно для построения геометрии соседних плиток и последующего
 * отсечения по видимой области.
 *
 * @param polygon исходный фундаментальный многоугольник
 * @param m изометрия диска (например, элемент из {@link enumerateNeighbors})
 * @returns многоугольник с преобразованными вершинами
 */
export function transformPolygon(
  polygon: FundamentalPolygon,
  m: Mobius,
): FundamentalPolygon {
  return {
    ...polygon,
    vertices: polygon.vertices.map((vertex) => apply(m, vertex)),
  };
}

// ---------------------------------------------------------------------------
// Геометрия-зависимая, ориентация-зависимая склейка поверхности (Требования 6, 8)
//
// Ниже — обобщение {@link computeSidePairing}/{@link enumerateNeighbors} на все
// три геометрии (гиперболическую, евклидову, сферическую) и на неориентируемые
// склейки. Образующие строятся через {@link Geometry.sidePairingIsometry}, что
// не зависит от модели Пуанкаре (в отличие от `translateToOrigin`, требующего
// строго внутренних точек диска и потому бросавшего исключение для евклидовых
// вершин на |z| = 1). Обращающие ориентацию пары (`x…x`) несут признак
// `orientationReversing`, а перечисление соседей и их применение честно
// учитывают антиголоморфность через {@link Geometry.compose}/{@link Geometry.apply}.
//
// Прежние функции (`computeSidePairing`, `enumerateNeighbors`, `transformPolygon`)
// сохранены без изменений для обратной совместимости (гиперболический,
// ориентируемый путь и его тесты).
// ---------------------------------------------------------------------------

/**
 * Склейка поверхности: образующие фуксовой/кристаллографической группы как
 * обобщённые изометрии {@link Isometry} (матрица линейной части + признак
 * обращения ориентации) для произвольной геометрии.
 */
export interface SurfacePairing {
  /** Образующие по одной на пару сторон (в порядке появления букв в слове). */
  generators: Generator[];
  /** Преобразование по метке: строчные → образующие, заглавные → обратные. */
  byLabel: Map<string, Isometry>;
  /** Все преобразования (образующие и обратные) единым массивом (для замощения). */
  all: Isometry[];
  /** Ориентируемость поверхности (`false`, если есть пара `x…x`). */
  orientable: boolean;
}

/**
 * Проверить, что построенная образующая переводит концы исходной стороны в
 * концы парной в метрике геометрии (Требования 8.4, 2.6). Для сохраняющей
 * ориентацию пары `g(p0) = q1`, `g(p1) = q0` (обмен концов из-за
 * противоположной ориентации границы); для обращающей ориентацию —
 * `T(p0) = q0`, `T(p1) = q1` (сопряжение уже обеспечивает обращение, концы не
 * меняются местами). Бросает ошибку только при нечисловом образе или грубом
 * несовпадении, чтобы отклонить заведомо некорректную склейку, не срывая
 * загрузку корректных карт.
 */
function verifySurfacePairing(
  geometry: Geometry,
  polygon: FundamentalPolygon,
  pair: ParsedPair,
  iso: Isometry,
  tolerance: number,
): void {
  const [p0, p1] = sideEndpoints(polygon, pair.sourceSide);
  const [q0, q1] = sideEndpoints(polygon, pair.targetSide);
  const image0 = geometry.apply(iso, p0);
  const image1 = geometry.apply(iso, p1);
  const expected0 = pair.orientationReversing ? q0 : q1;
  const expected1 = pair.orientationReversing ? q1 : q0;
  const tol = Math.max(tolerance, 1e-6);
  const finite =
    Number.isFinite(image0.re) &&
    Number.isFinite(image0.im) &&
    Number.isFinite(image1.re) &&
    Number.isFinite(image1.im);
  if (
    !finite ||
    !complexEquals(image0, expected0, tol) ||
    !complexEquals(image1, expected1, tol)
  ) {
    throw new Error(
      `Образующая '${pair.label}' не переводит сторону ${pair.sourceSide} в парную ` +
        `сторону ${pair.targetSide} в геометрии '${geometry.type}'`,
    );
  }
}

/**
 * Вычислить склейку сторон как обобщённые изометрии для заданной геометрии
 * (Требования 6, 8). Образующие строятся через {@link Geometry.sidePairingIsometry}:
 * - сохраняющая ориентацию пара (`x…X`) — голоморфная изометрия `g` с
 *   `g(p0) = q1`, `g(p1) = q0`;
 * - обращающая ориентацию пара (`x…x`) — антиголоморфная `T(z) = M(z̄)` с
 *   `T(p0) = q0`, `T(p1) = q1`, где `M` строится по сопряжённым концам
 *   исходной стороны (`orientationReversing = true`).
 *
 * В отличие от {@link computeSidePairing}, работает для евклидовой (вершины на
 * `|z| = 1`) и сферической геометрий и не бросает исключение из-за ограничения
 * модели Пуанкаре.
 *
 * @param polygon фундаментальный многоугольник
 * @param pairingSpec схема склейки (слово нормализуется)
 * @param geometry реализация геометрии (по умолчанию гиперболическая)
 * @param tolerance допуск проверки склейки
 */
export function computeSurfacePairing(
  polygon: FundamentalPolygon,
  pairingSpec: SidePairingSpec,
  geometry: Geometry = hyperbolicGeometry,
  tolerance = PAIRING_TOLERANCE,
): SurfacePairing {
  const word = normalizePairingWord(pairingSpec.word);
  const { pairs, orientable } = parsePairing(word, polygon.vertices.length);

  const generators: Generator[] = [];
  const byLabel = new Map<string, Isometry>();
  const all: Isometry[] = [];

  for (const pair of pairs) {
    const [p0, p1] = sideEndpoints(polygon, pair.sourceSide);
    const [q0, q1] = sideEndpoints(polygon, pair.targetSide);
    const iso: Isometry = pair.orientationReversing
      ? {
          // T(z) = M(z̄), T(p_k) = q_k ⇒ M: (p̄0, p̄1) ↦ (q0, q1).
          matrix: geometry.sidePairingIsometry(conjugate(p0), conjugate(p1), q0, q1).matrix,
          orientationReversing: true,
        }
      : {
          // Голоморфная склейка: g(p0) = q1, g(p1) = q0.
          matrix: geometry.sidePairingIsometry(p0, p1, q1, q0).matrix,
          orientationReversing: false,
        };

    verifySurfacePairing(geometry, polygon, pair, iso, tolerance);

    const generator: Generator = {
      label: pair.label,
      inverseLabel: pair.inverseLabel,
      transform: iso.matrix,
      sourceSide: pair.sourceSide,
      targetSide: pair.targetSide,
      orientationReversing: pair.orientationReversing,
    };
    const inverse = geometry.invert(iso);
    generators.push(generator);
    byLabel.set(pair.label, iso);
    byLabel.set(pair.inverseLabel, inverse);
    all.push(iso, inverse);
  }

  return { generators, byLabel, all, orientable };
}

/** Опорные точки для дедупликации элементов группы по образам (внутри диска). */
const ISOMETRY_KEY_POINTS: readonly Complex[] = [
  { re: 0, im: 0 },
  { re: 0.5, im: 0 },
  { re: 0, im: 0.5 },
];

/**
 * Канонический ключ обобщённой изометрии для дедупликации при переборе соседей.
 *
 * Изометрия однозначно определяется образами трёх различных точек, поэтому ключ
 * строится из квантованных образов {@link ISOMETRY_KEY_POINTS} плюс признак
 * обращения ориентации. Такой ключ:
 * - геометрия-независим (работает для аффинных, SU(1,1) и SU(2) действий);
 * - инвариантен к знаковой неоднозначности матрицы (`M` и `−M` дают один образ);
 * - различает голоморфные и антиголоморфные элементы (по флагу).
 */
function isometryKey(geometry: Geometry, iso: Isometry, tolerance: number): string {
  const q = (x: number): number => {
    const r = Math.round(x / tolerance) * tolerance;
    return Object.is(r, -0) ? 0 : r;
  };
  const parts: string[] = [iso.orientationReversing ? 'R' : 'D'];
  for (const p of ISOMETRY_KEY_POINTS) {
    const w = geometry.apply(iso, p);
    parts.push(`${q(w.re)},${q(w.im)}`);
  }
  return parts.join('|');
}

/**
 * Перечислить элементы группы склейки (соседние плитки) как обобщённые изометрии
 * поиском в ширину, честно учитывая антиголоморфность через
 * {@link Geometry.compose} (Требования 4, 6, 8). Обобщение
 * {@link enumerateNeighbors} на все геометрии и неориентируемые склейки.
 *
 * @param pairing результат {@link computeSurfacePairing}
 * @param options параметры перебора (глубина, ограничение числа, допуск дедупликации)
 * @param geometry реализация геометрии (по умолчанию гиперболическая)
 * @returns список уникальных элементов группы (включая тождество первым)
 */
export function enumerateSurfaceNeighbors(
  pairing: SurfacePairing,
  options: NeighborEnumerationOptions,
  geometry: Geometry = hyperbolicGeometry,
): Isometry[] {
  const { maxDepth } = options;
  if (!Number.isInteger(maxDepth) || maxDepth < 0) {
    throw new Error(`maxDepth должен быть целым числом ≥ 0, получено: ${maxDepth}`);
  }
  const maxCount = options.maxCount ?? Number.POSITIVE_INFINITY;
  const tolerance = options.dedupeTolerance ?? DEFAULT_DEDUPE_TOLERANCE;
  const moves = pairing.all;

  const found: Isometry[] = [];
  const seen = new Set<string>();

  const id = isometryIdentity();
  found.push(id);
  seen.add(isometryKey(geometry, id, tolerance));

  let frontier: Isometry[] = [id];
  for (let depth = 1; depth <= maxDepth && found.length < maxCount; depth++) {
    const next: Isometry[] = [];
    for (const current of frontier) {
      if (found.length >= maxCount) break;
      for (const move of moves) {
        if (found.length >= maxCount) break;
        const candidate = geometry.compose(current, move);
        const key = isometryKey(geometry, candidate, tolerance);
        if (seen.has(key)) continue;
        seen.add(key);
        found.push(candidate);
        next.push(candidate);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }

  return Number.isFinite(maxCount) ? found.slice(0, maxCount) : found;
}

/**
 * Применить обобщённую изометрию к каждой вершине многоугольника (образ-плитка),
 * с учётом геометрии и обращения ориентации. Обобщение {@link transformPolygon}.
 *
 * @param polygon исходный фундаментальный многоугольник
 * @param iso обобщённая изометрия (например, из {@link enumerateSurfaceNeighbors})
 * @param geometry реализация геометрии (по умолчанию гиперболическая)
 */
export function transformPolygonIsometry(
  polygon: FundamentalPolygon,
  iso: Isometry,
  geometry: Geometry = hyperbolicGeometry,
): FundamentalPolygon {
  return {
    ...polygon,
    vertices: polygon.vertices.map((vertex) => geometry.apply(iso, vertex)),
  };
}

/**
 * Проверить, что копия фундаментальной области (образ под изометрией `iso`)
 * имеет хотя бы одну общую вершину с исходной областью.
 *
 * Каждая вершина базового многоугольника переводится изометрией и сравнивается
 * (по евклидову расстоянию в координатах диска, с допуском `tolerance`) со всеми
 * вершинами базового многоугольника. Совпадение хотя бы одной пары означает, что
 * копия примыкает к области в вершине (общая вершина замощения). Так как склейка
 * отождествляет вершины многоугольника, копии вершинного кольца дают именно такие
 * совпадения.
 *
 * @param baseVertices вершины фундаментального многоугольника (координаты диска)
 * @param iso изометрия копии `γ`
 * @param geometry реализация геометрии (действие изометрии)
 * @param tolerance допуск совпадения вершин
 */
function sharesFundamentalVertex(
  baseVertices: Complex[],
  iso: Isometry,
  geometry: Geometry,
  tolerance: number,
): boolean {
  for (const vertex of baseVertices) {
    const image = geometry.apply(iso, vertex);
    for (const base of baseVertices) {
      if (Math.hypot(image.re - base.re, image.im - base.im) <= tolerance) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Перечислить копии фундаментальной области «вершинным кольцом»: все элементы
 * группы склейки `γ`, образ области под которыми имеет общую вершину с исходной
 * областью (Требование к круговому обходу вокруг многоугольника).
 *
 * В отличие от {@link enumerateSurfaceNeighbors} (BFS, ограниченный длиной слова
 * `maxDepth`), обход здесь ограничен геометрически: раскрываются только копии,
 * примыкающие к области в вершине. Это гарантирует, что вокруг каждой вершины
 * многоугольника собирается полный цикл соседних копий (в том числе с
 * «противоположной» стороны области), которые при обходе по длине слова малой
 * глубины не попадают в результат. Уход «вглубь» замощения не выполняется: копии,
 * не имеющие общей вершины с областью, отбрасываются и не раскрываются.
 *
 * Алгоритм — BFS по образующим `moves` от тождества; кандидат сохраняется и
 * раскрывается только если {@link sharesFundamentalVertex} истинно. Так как
 * последовательные копии вокруг общей вершины являются рёберными соседями (и
 * потому делят эту вершину), полный цикл вокруг каждой вершины достижим по
 * вершинно-примыкающим копиям. Вершинное кольцо конечно, поэтому фронт BFS
 * естественно вырождается; {@link VERTEX_TRAVERSAL_SAFETY_DEPTH} и `maxCount`
 * служат страховочными пределами.
 *
 * Тождество всегда включается первым элементом.
 *
 * @param polygon фундаментальный многоугольник (вершины — координаты диска)
 * @param moves преобразования склейки (образующие и обратные), например
 *   {@link SurfacePairing.all} или поднятые {@link SidePairingResult.all}
 * @param options параметры перебора (`maxCount`, `dedupeTolerance`, `vertexTolerance`)
 * @param geometry реализация геометрии (по умолчанию гиперболическая)
 * @returns список уникальных элементов группы вершинного кольца (с тождеством первым)
 */
export function enumerateVertexNeighbors(
  polygon: FundamentalPolygon,
  moves: Isometry[],
  options: NeighborEnumerationOptions,
  geometry: Geometry = hyperbolicGeometry,
): Isometry[] {
  const maxCount = options.maxCount ?? Number.POSITIVE_INFINITY;
  const tolerance = options.dedupeTolerance ?? DEFAULT_DEDUPE_TOLERANCE;
  const vertexTolerance = options.vertexTolerance ?? DEFAULT_VERTEX_TOLERANCE;
  const baseVertices = polygon.vertices;

  // Центроид области (для правильного многоугольника — центр диска). Служит для
  // ПРОСТРАНСТВЕННОЙ дедупликации копий: в корректном замощении каждой позиции
  // соответствует ровно одна копия. Поэтому элементы, отображающие область на
  // уже занятое место, отбрасываются. Это критично для неориентируемых и
  // орбифолдных склеек (например, восьмиугольник `abABcdcd`), группа которых
  // содержит торсион — нетривиальные повороты/отражения, ФИКСИРУЮЩИЕ центр и
  // переставляющие треугольные секторы области. Без пространственного дедупа
  // такие артефакты попадают в кольцо и дублируют рисунок по секторам уже
  // покрытой копии (Требование 8). Дедуп по матрице (`isometryKey`) их не ловит:
  // поворот вокруг центра — отдельная изометрия с другим ключом.
  let cx = 0;
  let cy = 0;
  for (const v of baseVertices) {
    cx += v.re;
    cy += v.im;
  }
  const n = baseVertices.length || 1;
  const centroid: Complex = { re: cx / n, im: cy / n };
  const centroidKey = (p: Complex): string => {
    const q = (x: number): number => {
      const r = Math.round(x / vertexTolerance) * vertexTolerance;
      return Object.is(r, -0) ? 0 : r;
    };
    return `${q(p.re)},${q(p.im)}`;
  };

  const found: Isometry[] = [];
  const seen = new Set<string>();
  const seenCentroids = new Set<string>();

  const id = isometryIdentity();
  found.push(id);
  seen.add(isometryKey(geometry, id, tolerance));
  seenCentroids.add(centroidKey(geometry.apply(id, centroid)));

  let frontier: Isometry[] = [id];
  for (
    let depth = 1;
    depth <= VERTEX_TRAVERSAL_SAFETY_DEPTH && found.length < maxCount;
    depth++
  ) {
    const next: Isometry[] = [];
    for (const current of frontier) {
      if (found.length >= maxCount) break;
      for (const move of moves) {
        if (found.length >= maxCount) break;
        const candidate = geometry.compose(current, move);
        const key = isometryKey(geometry, candidate, tolerance);
        if (seen.has(key)) continue;
        seen.add(key);
        // Пространственный дедуп: если копия отображает область на уже занятое
        // место (тот же образ центроида), это избыточный путь или торсионный/
        // реляторный артефакт, перекрывающий существующую копию — пропускаем и
        // не раскрываем (иначе дублирование рисунка по секторам).
        const cKey = centroidKey(geometry.apply(candidate, centroid));
        if (seenCentroids.has(cKey)) continue;
        // Сохраняем и раскрываем только копии, примыкающие к области в вершине:
        // это удерживает обход на «кольце» вокруг многоугольника и не даёт ему
        // уходить вглубь замощения.
        if (!sharesFundamentalVertex(baseVertices, candidate, geometry, vertexTolerance)) {
          continue;
        }
        seenCentroids.add(cKey);
        found.push(candidate);
        next.push(candidate);
      }
    }
    if (next.length === 0) break;
    frontier = next;
  }

  return Number.isFinite(maxCount) ? found.slice(0, maxCount) : found;
}
